import {
  property,
  subclass
} from '@arcgis/core/core/accessorSupport/decorators';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Handles from '@arcgis/core/core/Handles';
import { NGXLogger } from 'ngx-logger';
import {
  JepGroupLayer,
  JepGroupLayerProperties,
  JepLayer
} from './jep-arcgis.types';

export type PseudoLayerProperties<T extends JepLayer> =
  JepGroupLayerProperties & {
    parentLayer: T;
    logger?: NGXLogger;
  };

@subclass('jmkw.layers.PseudoLayer')
export class PseudoLayer<T extends JepLayer> extends JepGroupLayer {
  @property()
  protected parentLayer!: T;

  @property()
  logger?: NGXLogger;

  protected readonly handles = new Handles();
  private _pendingConfigurations?: Partial<this>;

  constructor(props: PseudoLayerProperties<T>) {
    super(props);

    const visHandle = reactiveUtils.watch(
      () => this.visible,
      (visible) => this.jepTools?.toggleVisibility?.(visible)
    );

    const readyHandle = reactiveUtils.watch(
      () => this.parentLayer?.loaded,
      (isReady) => {
        if (isReady && this._pendingConfigurations) {
          this._applyPendingConfigurations();
        }
      },
      { initial: true }
    );

    this.handles.add([visHandle, readyHandle]);

    // Optionally mirror into parent if it exposes addHandles:
    this.parentLayer?.addHandles?.([visHandle, readyHandle]);
  }

  /**
   * Apply configuration to this layer, buffering if parent isn't ready
   */
  applyConfiguration(config: Partial<this>): void {
    if (this.parentLayer?.loaded) {
      this._applyConfigImmediate(config);
    } else {
      // Buffer configuration until parent is ready
      this._pendingConfigurations = {
        ...this._pendingConfigurations,
        ...config
      };
    }
  }

  private _applyPendingConfigurations(): void {
    if (this._pendingConfigurations) {
      this._applyConfigImmediate(this._pendingConfigurations);
      this._pendingConfigurations = undefined;
    }
  }

  private _applyConfigImmediate(config: Partial<this>): void {
    const { layers: layersConfig, ...otherConfig } = config as any;

    // Apply non-layer properties
    Object.entries(otherConfig).forEach(([key, value]) => {
      this._setValueSafely(key, value);

      // Trigger jepTools visibility update when visible property is set
      if (key === 'visible' && this.jepTools?.toggleVisibility) {
        this.jepTools.toggleVisibility(!!value);
      }
    });

    // Apply nested layer configurations
    if (layersConfig && this.layers) {
      this._applyNestedLayerConfigurations(layersConfig);
    }
  }

  private _setValueSafely(key: string, value: any): void {
    try {
      const currentValue = (this as any)[key];

      if (currentValue?.next) {
        // Subject
        currentValue.next(value);
      } else if (currentValue?.set) {
        // WritableSignal
        currentValue.set(value);
      } else {
        // Regular property
        (this as any)[key] = value;
      }
    } catch (error) {
      if (this.logger) {
        this.logger.error(`[PseudoLayer] Error setting ${key}:`, error);
      } else {
        console.error(`[PseudoLayer] Error setting ${key}:`, error);
      }
    }
  }

  private _applyNestedLayerConfigurations(layersConfig: any): void {
    if (!this.layers?.forEach) return;

    this.layers.forEach((sublayer: any) => {
      const sublayerConfig = layersConfig[sublayer?.id];
      if (!sublayerConfig) return;

      if (sublayer.applyConfiguration) {
        // Recursive application for nested PseudoLayers
        sublayer.applyConfiguration(sublayerConfig);
      } else {
        // Direct application for regular layers
        const { layers: nestedLayers, ...directConfig } = sublayerConfig;
        Object.assign(sublayer, directConfig);

        if (nestedLayers && sublayer.layers) {
          this._applyNestedLayerConfigurations.call(sublayer, nestedLayers);
        }
      }
    });
  }

  override destroy(): void {
    try {
      this.handles.removeAll();
    } finally {
      super.destroy();
    }
  }
}
